import { computed, ref, watch } from 'vue'

import type { ContentItemLinkDetails } from '../../../../types'
import type { ContentItem } from '../content-item-details.types'

export type UseActiveContentItemOptions = {
  data: () => ContentItemLinkDetails | null
  contentItemId: () => string | null
  onChange: (contentItemId: string) => void
}

export const useActiveContentItem = ({
  data,
  contentItemId,
  onChange,
}: UseActiveContentItemOptions) => {
  const activeContentItem = ref<ContentItem | undefined>()

  watch(
    [data, contentItemId],
    ([nextData, id]) => {
      if (!nextData) {
        activeContentItem.value = undefined
        return
      }
      const stillPresent = nextData.contentItems.some(
        (item) => item.id === activeContentItem.value?.id,
      )
      if (!stillPresent) {
        activeContentItem.value = nextData.contentItems.find((item) => item.id === id)
      }
    },
    { immediate: true },
  )

  watch(
    () => data()?.contentItems.find((item) => item.id === activeContentItem.value?.id)?.status,
    (newStatus) => {
      if (newStatus && activeContentItem.value && activeContentItem.value.status !== newStatus) {
        activeContentItem.value.status = newStatus
      }
    },
  )

  const contentItems = computed(() => data()?.contentItems ?? [])

  const captionSegments = computed(() => {
    const text = activeContentItem.value?.text
    if (!text) {
      return []
    }
    return text
      .split(/([#@]\w+)/g)
      .filter(Boolean)
      .map((segment) => ({ text: segment, isTag: /^[#@]/.test(segment) }))
  })

  const setActiveContentItem = (item: ContentItem) => {
    activeContentItem.value = item
    onChange(item.id)
  }

  return { activeContentItem, contentItems, captionSegments, setActiveContentItem }
}
